import { useState } from 'react';
import { Photo, Tag } from '../../types';
import { Check, Eye, Maximize2 } from 'lucide-react';

interface PhotoCardProps {
  photo: Photo;
  selected?: boolean;
  showTags?: boolean;
  onClick?: () => void;
  onTagClick?: (tag: Tag) => void;
  onLightboxClick?: () => void;
  selectable?: boolean;
  showLightboxButton?: boolean;
}

export default function PhotoCard({
  photo,
  selected = false,
  showTags = true,
  onClick,
  onTagClick,
  onLightboxClick,
  selectable = false,
  showLightboxButton = false,
}: PhotoCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleTagClick = (tag: Tag, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTagClick) {
      onTagClick(tag);
    }
  };

  return (
    <div
      className={`relative group cursor-pointer transition-all duration-200 ${
        selected ? 'ring-2 ring-primary-500 ring-offset-2' : ''
      }`}
      onClick={handleClick}
    >
      <div className="card overflow-hidden">
        <div className="relative aspect-square bg-gray-100">
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-pulse bg-gray-200 w-full h-full"></div>
            </div>
          )}
          
          {imageError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="text-gray-400 text-center">
                <Eye className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">Image non disponible</p>
              </div>
            </div>
          ) : (
            <img
              src={photo.thumbnailUrl}
              alt={photo.originalName}
              className={`w-full h-full object-cover transition-opacity duration-200 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          )}
          
          {/* Selection indicator */}
          {selectable && (
            <div
              className={`absolute top-2 right-2 w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                selected
                  ? 'bg-primary-600 border-primary-600'
                  : 'bg-white border-gray-300 group-hover:border-primary-400'
              }`}
            >
              {selected && <Check className="h-4 w-4 sm:h-5 sm:w-5 text-white" />}
            </div>
          )}

          {/* Lightbox button - positioned bottom left to avoid selection conflict */}
          {showLightboxButton && onLightboxClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onLightboxClick();
              }}
              className="absolute bottom-2 left-2 w-7 h-7 sm:w-8 sm:h-8 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 z-10"
              title="Agrandir l'image"
            >
              <Maximize2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </button>
          )}
          
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200" />
        </div>
        
        {/* Photo info */}
        <div className="p-2.5 sm:p-3">
          <p className="text-xs sm:text-sm text-gray-500 truncate" title={photo.originalName}>
            {photo.originalName}
          </p>
          
          {/* Tags */}
          {showTags && photo.tags && photo.tags.length > 0 && (
            <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-1">
              {photo.tags.slice(0, 3).map((tag) => (
                <button
                  key={tag.id}
                  onClick={(e) => handleTagClick(tag, e)}
                  className="tag-chip hover:bg-primary-200 transition-colors text-xs px-2 py-0.5 sm:px-2.5 min-h-[20px] sm:min-h-[24px] flex items-center"
                  title={tag.name}
                >
                  <span className="truncate max-w-[80px] sm:max-w-none">{tag.name}</span>
                </button>
              ))}
              {photo.tags.length > 3 && (
                <span className="tag-chip bg-gray-100 text-gray-600 text-xs px-2 py-0.5 sm:px-2.5 min-h-[20px] sm:min-h-[24px] flex items-center">
                  +{photo.tags.length - 3}
                </span>
              )}
            </div>
          )}
          
          {/* No tags indicator */}
          {showTags && (!photo.tags || photo.tags.length === 0) && (
            <div className="mt-1.5 sm:mt-2">
              <span className="text-xs text-gray-400 italic">Aucun tag</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}